import { useEffect, useMemo, useState } from 'react'

import { ChevronDown, ChevronUp } from 'lucide-react'

import { KpiCard, KpiStrip } from '../cc'

import type { Tone } from '../cc/tokens'

import type { ReportRow } from '../lib/types'

import { BRIEFING_REPORT_IDS } from './reportCatalog'

import { prefetchReports } from './useReportData'

import { reportCard, reportCardPad, reportEyebrow } from './ui'



function num(v: unknown): number | null {

  if (typeof v === 'number' && Number.isFinite(v)) return v

  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)

  return null

}



function fmt(v: number | null | undefined): string {

  if (v == null) return '—'

  return Number.isInteger(v) ? String(v) : v.toFixed(1)

}



function briefingMetrics(brief: Record<string, ReportRow[]>) {

  const tat = brief.tat_headline?.[0]

  const trip = brief.trip_daily?.[0]

  const drv = brief.driver_scorecard?.[0]

  const pnl = brief.corridor_daily?.[0]

  const breachN = brief.tat_breach?.length ?? 0

  const stopN = (brief.unscheduled ?? []).reduce(

    (acc, r) => acc + (num(r.stops ?? r.count) ?? 0), 0,

  )

  const fleetN = brief.fleet_now?.length ?? 0

  const otd = num(tat?.on_time_pct ?? tat?.ontime_pct)

  let otdTone: Tone = 'neutral'

  if (otd != null) otdTone = otd >= 90 ? 'ok' : otd >= 75 ? 'warn' : 'danger'

  return {

    dayLabel: String(tat?.day ?? trip?.day ?? 'latest day'),

    otd: otd != null ? fmt(otd) : '—',

    otdTone,

    trips: fmt(num(trip?.trips ?? trip?.trip_count)),

    km: fmt(num(trip?.km ?? trip?.distance_odo_km ?? pnl?.km)),

    kwh: fmt(num(trip?.discharge_kwh ?? pnl?.discharge_kwh)),

    topDriver: String(drv?.driver_name ?? drv?.driver_id ?? '—'),

    topScore: drv?.score != null ? `score ${fmt(num(drv.score))}` : 'scorecard',

    pnl: pnl?.contribution_inr != null ? `₹${fmt(num(pnl.contribution_inr))}`

      : pnl?.revenue_inr != null ? `₹${fmt(num(pnl.revenue_inr))}` : '—',

    breaches: String(breachN),

    breachTone: (breachN === 0 ? 'ok' : breachN < 5 ? 'warn' : 'danger') as Tone,

    stops: String(stopN),

    stopsTone: (stopN === 0 ? 'ok' : stopN < 8 ? 'warn' : 'danger') as Tone,

    fleetHint: fleetN > 0 ? `Fleet now: ${fleetN} plates in reporting window.` : '',

  }

}



interface BriefingStripProps {

  collapsed?: boolean

  onToggle?: () => void

  refreshTick?: number

}



export function BriefingStrip({ collapsed, onToggle, refreshTick = 0 }: BriefingStripProps) {

  const [brief, setBrief] = useState<Record<string, ReportRow[]>>({})

  const [updatedAt, setUpdatedAt] = useState<string | null>(null)



  useEffect(() => {

    void prefetchReports([...BRIEFING_REPORT_IDS]).then((data) => {

      setBrief(data)

      setUpdatedAt(new Date().toLocaleTimeString())

    })

  }, [refreshTick])



  const strip = useMemo(() => briefingMetrics(brief), [brief])



  return (

    <div className={`${reportCard} ${reportCardPad}`}>

      <div className="mb-4 flex items-center justify-between gap-3">

        <div>

          <div className={reportEyebrow}>Day briefing</div>

          <div className="mt-1 text-sm text-slate-600">

            {strip.dayLabel}

            {updatedAt ? (

              <span className="ml-2 font-mono text-xs text-slate-400">as of {updatedAt}</span>

            ) : null}

          </div>

        </div>

        {onToggle ? (

          <button

            type="button"

            onClick={onToggle}

            className="flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-xs text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 hover:text-slate-700"

          >

            {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}

            {collapsed ? 'Expand' : 'Collapse'}

          </button>

        ) : null}

      </div>

      {!collapsed ? (

        <>

          <KpiStrip>

            <KpiCard label="On-time" value={strip.otd} unit="%" sub="TAT headline" tone={strip.otdTone} />

            <KpiCard label="Trips today" value={strip.trips} sub="completed legs" />

            <KpiCard label="Corridor km" value={strip.km} sub="trip daily" />

            <KpiCard label="Energy kWh" value={strip.kwh} sub="discharge" />

            <KpiCard label="TAT breaches" value={strip.breaches} sub="open exceptions" tone={strip.breachTone} dot />

            <KpiCard label="Roadside stops" value={strip.stops} sub="unscheduled" tone={strip.stopsTone} dot />

            <KpiCard label="Top driver" value={strip.topDriver} sub={strip.topScore} tone="info" />

            <KpiCard label="Contribution" value={strip.pnl.replace(/^₹/, '')} unit={strip.pnl.startsWith('₹') ? '₹' : undefined} sub="daily P&L" tone="ok" />

          </KpiStrip>

          {strip.fleetHint ? (

            <p className="mt-4 text-sm text-slate-500">{strip.fleetHint}</p>

          ) : null}

        </>

      ) : null}

    </div>

  )

}


